import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import petBreedsService from '../../services/petBreedsService';

const initialState = {
  petBreeds: [],
  isLoading: false,
  error: null,
  success: false,
};

export const fetchPetBreeds = createAsyncThunk(
  'petBreeds/fetchAll',
  async (_, thunkAPI) => {
    try {
      return await petBreedsService.getAll();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch pet breeds';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchBreedsByType = createAsyncThunk(
  'petBreeds/fetchByType',
  async (typeId, thunkAPI) => {
    try {
      return await petBreedsService.getByType(typeId);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch breeds';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createPetBreed = createAsyncThunk(
  'petBreeds/create',
  async (data, thunkAPI) => {
    try {
      return await petBreedsService.create(data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create breed';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updatePetBreed = createAsyncThunk(
  'petBreeds/update',
  async ({ id, data }, thunkAPI) => {
    try {
      return await petBreedsService.update(id, data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update breed';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deletePetBreed = createAsyncThunk(
  'petBreeds/delete',
  async (id, thunkAPI) => {
    try {
      await petBreedsService.delete(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete breed';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const petBreedsSlice = createSlice({
  name: 'petBreeds',
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPetBreeds.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPetBreeds.fulfilled, (state, action) => {
        state.isLoading = false;
        state.petBreeds = action.payload;
      })
      .addCase(fetchPetBreeds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchBreedsByType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBreedsByType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.petBreeds = action.payload;
      })
      .addCase(fetchBreedsByType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createPetBreed.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPetBreed.fulfilled, (state, action) => {
        state.isLoading = false;
        state.petBreeds.push(action.payload);
        state.success = true;
      })
      .addCase(createPetBreed.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updatePetBreed.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePetBreed.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.petBreeds.findIndex((pb) => pb._id === action.payload._id);
        if (index !== -1) {
          state.petBreeds[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updatePetBreed.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deletePetBreed.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePetBreed.fulfilled, (state, action) => {
        state.isLoading = false;
        state.petBreeds = state.petBreeds.filter((pb) => pb._id !== action.payload);
        state.success = true;
      })
      .addCase(deletePetBreed.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess, clearError } = petBreedsSlice.actions;
export default petBreedsSlice.reducer;
